import { useState, useEffect, useRef } from "react";
import { X, ChevronLeft, ChevronRight, Keyboard, AlertTriangle } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { PdfComparison } from "../utils/compare-pdfs";

interface PdfPageViewerModalProps {
	comparisons: PdfComparison[];
	initialComparisonIndex: number;
	initialPageIndex: number;
	isOpen: boolean;
	onClose: () => void;
	reviewState: Map<string, boolean | undefined>;
	onUpdateReview: (comparison: PdfComparison, status: boolean | undefined) => void;
}

type ViewMode = "side-by-side" | "zip1" | "zip2" | "diff";

export const PdfPageViewerModal: React.FC<PdfPageViewerModalProps> = ({
	comparisons,
	initialComparisonIndex,
	initialPageIndex,
	isOpen,
	onClose,
	reviewState,
	onUpdateReview,
}) => {
	// Sort comparisons: PDFs with differences first, then without
	const sortedComparisons = [...comparisons].sort((a, b) => {
		if (a.hasAnyDifference && !b.hasAnyDifference) return -1;
		if (!a.hasAnyDifference && b.hasAnyDifference) return 1;
		return 0;
	});

	// Map initial index to sorted order
	const initialSortedIndex = sortedComparisons.findIndex(
		c => c.orgId === comparisons[initialComparisonIndex].orgId &&
		     c.pdfType === comparisons[initialComparisonIndex].pdfType
	);

	const [currentComparisonIndex, setCurrentComparisonIndex] = useState(initialSortedIndex >= 0 ? initialSortedIndex : 0);
	const [currentPageIndex, setCurrentPageIndex] = useState(initialPageIndex);
	const [viewMode, setViewMode] = useState<ViewMode>("side-by-side");
	const [showShortcuts, setShowShortcuts] = useState(false);
	const modalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const newSortedIndex = sortedComparisons.findIndex(
			c => c.orgId === comparisons[initialComparisonIndex].orgId &&
			     c.pdfType === comparisons[initialComparisonIndex].pdfType
		);
		setCurrentComparisonIndex(newSortedIndex >= 0 ? newSortedIndex : 0);
		setCurrentPageIndex(initialPageIndex);
	}, [initialComparisonIndex, initialPageIndex, comparisons]);

	useEffect(() => {
		if (isOpen && modalRef.current) {
			modalRef.current.focus();
		}
	}, [isOpen]);

	if (!isOpen || sortedComparisons.length === 0) return null;

	const currentComparison = sortedComparisons[currentComparisonIndex];
	const currentPage = currentComparison.pages[currentPageIndex];

	// Get current review status
	const getComparisonKey = (comparison: PdfComparison) =>
		`${comparison.orgId}::${comparison.pdfType}`;
	const currentReviewStatus = reviewState.get(getComparisonKey(currentComparison));

	const viewModes: ViewMode[] = ["side-by-side", "zip1", "zip2", "diff"];
	const currentViewModeIndex = viewModes.indexOf(viewMode);

	// View mode navigation
	const handlePrevViewMode = () => {
		const newIndex = currentViewModeIndex === 0 ? viewModes.length - 1 : currentViewModeIndex - 1;
		setViewMode(viewModes[newIndex]);
	};

	const handleNextViewMode = () => {
		const newIndex = currentViewModeIndex === viewModes.length - 1 ? 0 : currentViewModeIndex + 1;
		setViewMode(viewModes[newIndex]);
	};

	const setViewModeByKey = (key: string) => {
		const modeMap: Record<string, ViewMode> = {
			'q': 'side-by-side',
			'w': 'zip1',
			'e': 'zip2',
			'r': 'diff'
		};
		if (modeMap[key]) {
			setViewMode(modeMap[key]);
		}
	};

	// Page navigation
	const handlePrevPage = () => {
		if (currentPageIndex > 0) {
			setCurrentPageIndex(currentPageIndex - 1);
		}
	};

	const handleNextPage = () => {
		if (currentPageIndex < currentComparison.pages.length - 1) {
			setCurrentPageIndex(currentPageIndex + 1);
		}
	};

	// PDF navigation
	const handlePrevPdf = () => {
		if (currentComparisonIndex > 0) {
			setCurrentComparisonIndex(currentComparisonIndex - 1);
			setCurrentPageIndex(0); // Reset to first page of new PDF
		}
	};

	const handleNextPdf = () => {
		if (currentComparisonIndex < sortedComparisons.length - 1) {
			setCurrentComparisonIndex(currentComparisonIndex + 1);
			setCurrentPageIndex(0); // Reset to first page of new PDF
		}
	};

	const getViewModeLabel = (mode: ViewMode): string => {
		switch (mode) {
			case "side-by-side": return "Nebeneinander";
			case "zip1": return "ZIP 1";
			case "zip2": return "ZIP 2";
			case "diff": return "Unterschiede";
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		const key = e.key.toLowerCase();

		// View mode shortcuts
		if (['q', 'w', 'e', 'r'].includes(key)) {
			setViewModeByKey(key);
			e.preventDefault();
			return;
		}

		// Page navigation
		if (key === 'a') {
			handlePrevPage();
			e.preventDefault();
			return;
		}
		if (key === 's') {
			handleNextPage();
			e.preventDefault();
			return;
		}

		// PDF navigation
		if (e.key === 'ArrowUp') {
			handlePrevPdf();
			e.preventDefault();
			return;
		}
		if (e.key === 'ArrowDown') {
			handleNextPdf();
			e.preventDefault();
			return;
		}

		// Review shortcuts
		if (key === 'y') {
			onUpdateReview(currentComparison, true);
			e.preventDefault();
			return;
		}
		if (key === 'x') {
			onUpdateReview(currentComparison, false);
			e.preventDefault();
			return;
		}

		// Shortcuts legend
		if (key === '?') {
			setShowShortcuts(!showShortcuts);
			e.preventDefault();
			return;
		}

		// Close modal
		if (e.key === 'Escape') {
			onClose();
		}
	};

	return (
		<div
			ref={modalRef}
			className="fixed inset-0 z-50 bg-gray-200/90 flex items-center justify-center"
			onClick={onClose}
			onKeyDown={handleKeyDown}
			tabIndex={0}
			role="dialog"
			aria-modal="true"
			aria-label="PDF Seitenvergleich"
		>
			<div
				className="relative w-full h-full flex flex-col"
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header */}
				<div className="bg-white border-b border-gray-300 px-6 py-4 flex items-center justify-between shadow-md">
					<div className="flex items-center gap-4">
						<div>
							<h2 className="text-lg font-semibold text-gray-900">
								{currentComparison.orgId} - {currentComparison.pdfType}
							</h2>
							<p className="text-sm text-gray-600">
								PDF {currentComparisonIndex + 1} von {sortedComparisons.length} • Seite {currentPage.pageNumber} von {currentComparison.pages.length}
							</p>
						</div>

						{currentPage.hasDifference && (
							<div className="bg-red-100 border-2 border-red-600 rounded px-3 py-1">
								<span className="text-red-900 text-sm font-semibold">
									{currentPage.diffPercentage.toFixed(2)}% unterschiedlich
								</span>
							</div>
						)}

					{/* Review Status Badge */}
					{currentReviewStatus === true && (
						<div className="bg-green-100 border-2 border-green-600 rounded px-3 py-1">
							<span className="text-green-900 text-sm font-semibold">
								✓ Okay
							</span>
						</div>
					)}
					{currentReviewStatus === false && (
						<div className="bg-orange-100 border-2 border-orange-600 rounded px-3 py-1">
							<span className="text-orange-900 text-sm font-semibold">
								✗ Problematisch
							</span>
						</div>
					)}
					</div>

					<div className="flex items-center gap-4">
						{/* Keyboard Shortcuts Button */}
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setShowShortcuts(!showShortcuts)}
							aria-label="Tastaturkürzel"
							className="h-8"
						>
							<Keyboard className="w-4 h-4 mr-2" />
							?
						</Button>
						{/* View Mode Selector */}
						<div className="flex gap-2 bg-gray-200 rounded p-1">
							<Button
								variant={viewMode === "side-by-side" ? "default" : "ghost"}
								size="sm"
								onClick={() => setViewMode("side-by-side")}
								className="h-8"
							>
								Nebeneinander
							</Button>
							<Button
								variant={viewMode === "zip1" ? "default" : "ghost"}
								size="sm"
								onClick={() => setViewMode("zip1")}
								className="h-8"
							>
								ZIP 1
							</Button>
							<Button
								variant={viewMode === "zip2" ? "default" : "ghost"}
								size="sm"
								onClick={() => setViewMode("zip2")}
								className="h-8"
							>
								ZIP 2
							</Button>
							<Button
								variant={viewMode === "diff" ? "default" : "ghost"}
								size="sm"
								onClick={() => setViewMode("diff")}
								className="h-8"
							>
								Diff
							</Button>
						</div>

						<Button
							variant="ghost"
							size="sm"
							onClick={onClose}
							aria-label="Schließen"
						>
							<X className="w-5 h-5 text-gray-900" />
						</Button>
					</div>
				</div>

				{/* Content */}
				<div className="flex-1 overflow-auto p-8 flex items-center justify-center bg-gray-100">
					{viewMode === "side-by-side" ? (
						<div className="grid grid-cols-3 gap-6 w-fit max-w-[95%]">
							<div className="flex flex-col">
								<h3 className="text-sm font-medium text-gray-900 mb-2 text-center">
									ZIP 1 (Referenz)
								</h3>
								{!currentPage.hasPage1 && (
									<div className="bg-orange-100 border-2 border-orange-600 rounded p-3 mb-2 flex items-center gap-2 justify-center">
										<AlertTriangle className="w-4 h-4 text-orange-900" />
										<span className="text-sm font-semibold text-orange-900">
											Seite nicht verfügbar
										</span>
									</div>
								)}
								<div className="bg-white rounded overflow-hidden shadow-lg">
									<img
										src={currentPage.page1Canvas.toDataURL()}
										alt={`ZIP 1 - Seite ${currentPage.pageNumber}`}
										className={`h-auto ${!currentPage.hasPage1 ? 'opacity-30' : ''}`}
										style={{ width: 'auto', maxHeight: 'calc(100vh - 300px)' }}
									/>
								</div>
							</div>

							<div className="flex flex-col">
								<h3 className="text-sm font-medium text-gray-900 mb-2 text-center">
									ZIP 2 (Vergleich)
								</h3>
								{!currentPage.hasPage2 && (
									<div className="bg-orange-100 border-2 border-orange-600 rounded p-3 mb-2 flex items-center gap-2 justify-center">
										<AlertTriangle className="w-4 h-4 text-orange-900" />
										<span className="text-sm font-semibold text-orange-900">
											Seite nicht verfügbar
										</span>
									</div>
								)}
								<div className="bg-white rounded overflow-hidden shadow-lg">
									<img
										src={currentPage.page2Canvas.toDataURL()}
										alt={`ZIP 2 - Seite ${currentPage.pageNumber}`}
										className={`h-auto ${!currentPage.hasPage2 ? 'opacity-30' : ''}`}
										style={{ width: 'auto', maxHeight: 'calc(100vh - 300px)' }}
									/>
								</div>
							</div>

							<div className="flex flex-col">
								<h3 className="text-sm font-medium text-red-900 mb-2 text-center">
									Unterschiede
								</h3>
								<div className="bg-white rounded overflow-hidden shadow-lg">
									<img
										src={currentPage.diffCanvas.toDataURL()}
										alt={`Unterschiede - Seite ${currentPage.pageNumber}`}
										className="h-auto"
										style={{ width: 'auto', maxHeight: 'calc(100vh - 300px)' }}
									/>
								</div>
							</div>
						</div>
					) : viewMode === "zip1" ? (
						<div className="flex flex-col items-center gap-4">
							{!currentPage.hasPage1 && (
								<div className="bg-orange-100 border-2 border-orange-600 rounded p-4 flex items-center gap-3">
									<AlertTriangle className="w-5 h-5 text-orange-900" />
									<span className="text-sm font-semibold text-orange-900">
										Diese Seite ist in ZIP 1 nicht verfügbar
									</span>
								</div>
							)}
							<div className="bg-white rounded overflow-hidden shadow-2xl">
								<img
									src={currentPage.page1Canvas.toDataURL()}
									alt={`ZIP 1 - Seite ${currentPage.pageNumber}`}
									className={`h-auto ${!currentPage.hasPage1 ? 'opacity-30' : ''}`}
									style={{ width: 'auto', maxHeight: 'calc(100vh - 200px)', maxWidth: '95vw' }}
								/>
							</div>
						</div>
					) : viewMode === "zip2" ? (
						<div className="flex flex-col items-center gap-4">
							{!currentPage.hasPage2 && (
								<div className="bg-orange-100 border-2 border-orange-600 rounded p-4 flex items-center gap-3">
									<AlertTriangle className="w-5 h-5 text-orange-900" />
									<span className="text-sm font-semibold text-orange-900">
										Diese Seite ist in ZIP 2 nicht verfügbar
									</span>
								</div>
							)}
							<div className="bg-white rounded overflow-hidden shadow-2xl">
								<img
									src={currentPage.page2Canvas.toDataURL()}
									alt={`ZIP 2 - Seite ${currentPage.pageNumber}`}
									className={`h-auto ${!currentPage.hasPage2 ? 'opacity-30' : ''}`}
									style={{ width: 'auto', maxHeight: 'calc(100vh - 200px)', maxWidth: '95vw' }}
								/>
							</div>
						</div>
					) : (
						<div className="bg-white rounded overflow-hidden shadow-2xl">
							<img
								src={currentPage.diffCanvas.toDataURL()}
								alt={`Unterschiede - Seite ${currentPage.pageNumber}`}
								className="h-auto"
								style={{ width: 'auto', maxHeight: 'calc(100vh - 200px)', maxWidth: '95vw' }}
							/>
						</div>
					)}
				</div>

				{/* Keyboard Shortcuts Legend */}
				{showShortcuts && (
					<div className="absolute inset-0 bg-gray-100/95 flex items-center justify-center z-10" onClick={() => setShowShortcuts(false)}>
						<div className="bg-white rounded-lg shadow-xl border-2 border-gray-300 p-8 max-w-2xl" onClick={(e) => e.stopPropagation()}>
							<div className="flex items-center justify-between mb-6">
								<h3 className="text-xl font-semibold text-gray-900">
									Tastaturkürzel
								</h3>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setShowShortcuts(false)}
								>
									<X className="w-5 h-5 text-gray-900" />
								</Button>
							</div>

							<div className="grid grid-cols-2 gap-6 text-sm">
								<div>
									<h4 className="font-semibold text-gray-900 mb-3">Ansichtsmodi</h4>
									<div className="space-y-2">
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Nebeneinander</span>
											<kbd className="px-2 py-1 bg-gray-200 rounded text-gray-900 border border-gray-400 font-mono text-xs">Q</kbd>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-gray-600">ZIP 1</span>
											<kbd className="px-2 py-1 bg-gray-200 rounded text-gray-900 border border-gray-400 font-mono text-xs">W</kbd>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-gray-600">ZIP 2</span>
											<kbd className="px-2 py-1 bg-gray-200 rounded text-gray-900 border border-gray-400 font-mono text-xs">E</kbd>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Diff</span>
											<kbd className="px-2 py-1 bg-gray-200 rounded text-gray-900 border border-gray-400 font-mono text-xs">R</kbd>
										</div>
									</div>
								</div>

								<div>
									<h4 className="font-semibold text-gray-900 mb-3">Seitennavigation</h4>
									<div className="space-y-2">
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Vorherige Seite</span>
											<kbd className="px-2 py-1 bg-gray-200 rounded text-gray-900 border border-gray-400 font-mono text-xs">A</kbd>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Nächste Seite</span>
											<kbd className="px-2 py-1 bg-gray-200 rounded text-gray-900 border border-gray-400 font-mono text-xs">S</kbd>
										</div>
									</div>
								</div>

								<div>
									<h4 className="font-semibold text-gray-900 mb-3">PDF-Navigation</h4>
									<div className="space-y-2">
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Vorheriges PDF</span>
											<kbd className="px-2 py-1 bg-gray-200 rounded text-gray-900 border border-gray-400 font-mono text-xs">↑</kbd>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Nächstes PDF</span>
											<kbd className="px-2 py-1 bg-gray-200 rounded text-gray-900 border border-gray-400 font-mono text-xs">↓</kbd>
										</div>
									</div>
								</div>

								<div>
									<h4 className="font-semibold text-gray-900 mb-3">Review</h4>
									<div className="space-y-2">
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Als okay markieren</span>
											<kbd className="px-2 py-1 bg-gray-200 rounded text-gray-900 border border-gray-400 font-mono text-xs">Y</kbd>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Als problematisch markieren</span>
											<kbd className="px-2 py-1 bg-gray-200 rounded text-gray-900 border border-gray-400 font-mono text-xs">X</kbd>
										</div>
									</div>
								</div>

								<div>
									<h4 className="font-semibold text-gray-900 mb-3">Allgemein</h4>
									<div className="space-y-2">
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Hilfe anzeigen</span>
											<kbd className="px-2 py-1 bg-gray-200 rounded text-gray-900 border border-gray-400 font-mono text-xs">?</kbd>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-gray-600">Schließen</span>
											<kbd className="px-2 py-1 bg-gray-200 rounded text-gray-900 border border-gray-400 font-mono text-xs">ESC</kbd>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Navigation */}
				<div className="bg-white border-t border-gray-300 shadow-md px-6 py-4 flex items-center justify-between">
					<Button
						variant="outline"
						onClick={handlePrevViewMode}
						aria-label="Vorherige Ansicht"
					>
						<ChevronLeft className="w-5 h-5 mr-2" />
						Vorherige Ansicht
					</Button>

					<div className="text-gray-900 text-sm font-medium">
						{getViewModeLabel(viewMode)}
					</div>

					<Button
						variant="outline"
						onClick={handleNextViewMode}
						aria-label="Nächste Ansicht"
					>
						Nächste Ansicht
						<ChevronRight className="w-5 h-5 ml-2" />
					</Button>
				</div>
			</div>
		</div>
	);
};
